/// <amd-module name="TFS/Admin/Views/ProfileSettings/SSHPublicKeysView/SSHPublicKeysView" />
import React = require("react");

import { equals, localeFormat } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent, Components } from "VSS/Platform/Layout";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Header, TitleSize, HeaderDescription } from "VSSUI/Header";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { ObservableValue, ObservableArray } from "VSS/Core/Observable";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

import * as Resources from "TFS/Admin/Views/ProfileSettings/SSHPublicKeysView/Resources";
import * as SSHKeysDataSource from "TFS/Admin/Views/ProfileSettings/SSHPublicKeysView/Sources/SSHPublicKeysDataSource";
import SSHPublicKeysTableView from "TFS/Admin/Views/ProfileSettings/SSHPublicKeysView/Components/SSHPublicKeysTableView";
import DisabledSSHView from "TFS/Admin/Views/ProfileSettings/SSHPublicKeysView/Components/DisabledSSHPublicKeysView";
import SSHPublicKeysZeroData from "TFS/Admin/Views/ProfileSettings/SSHPublicKeysView/Components/NoPublicKeysView";
import AddNewKeyPanel from "TFS/Admin/Views/ProfileSettings/SSHPublicKeysView/Components/AddNewKeyPanel";
import DisplaySSHKeyDataPanel from "TFS/Admin/Views/ProfileSettings/SSHPublicKeysView/Components/DisplaySSHKeyDataPanel";
import DeleteSSHKeyDialog from "TFS/Admin/Views/ProfileSettings/SSHPublicKeysView/Components/DeleteSSHKeyDialog";
import ServerFingerprintCard from "TFS/Admin/Views/ProfileSettings/SSHPublicKeysView/Components/ServerFingerprintCard";
import { CleanErrorMessage } from "TFS/Admin/Common/Util/CustomErrorHelper";
import { SSHToken } from "TFS/Admin/Views/ProfileSettings/SSHPublicKeysView/Contracts";
import { SessionToken } from "TFS/Admin/Common/Contracts/Token";
import { getTokenClient } from "TFS/Admin/Common/RestClient/Token";
import { AdminTelemetry, AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { sendToast } from "TFS/Admin/Common/Util/SendToastUtility";

export default class SSHPublicKeysComponent extends VssComponent<{}, {}> {
    private readonly issueSessionTokenContributionId = "ms.vss-token-web.personal-access-token-issue-session-token-provider";

    private _telemetryLogger = new AdminTelemetry(this.context.pageContext);
    private _isFeatureEnabled = new ObservableValue<boolean>(true);
    private _tokenData = new ObservableArray<SSHToken>();
    private _showAddPanel = new ObservableValue<boolean>(false);
    private _showDeleteDialog = new ObservableValue<boolean>(false);
    private _showDetails = new ObservableValue<boolean>(false);
    private _errorMessage = new ObservableValue<string>("");
    private _selectedToken?: SSHToken;
    private _pageData: any;

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);
        this._isFeatureEnabled.value = isFeatureFlagEnabled(this.context.pageContext, "WebAccess.SshUI", false);
    }

    public componentDidMount() {
        if (this._isFeatureEnabled.value) {
            this._fetchInitialData();
        }
    }

    public render() {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow">
                    <Observer isFeatureEnabled={this._isFeatureEnabled}>
                        {(props: { isFeatureEnabled: boolean }) => (
                            <Header
                                title={Resources.SSHPublicKeysTitle}
                                commandBarItems={
                                    props.isFeatureEnabled
                                        ? [
                                              {
                                                  id: "newKey",
                                                  iconProps: { iconName: Resources.Add },
                                                  ariaLabel: Resources.NewKey,
                                                  onActivate: () => this._onShowAddPanel(),
                                                  text: Resources.NewKey,
                                                  role: "button",
                                                  isPrimary: true
                                              }
                                          ]
                                        : []
                                }
                                titleSize={TitleSize.Large}
                            />
                        )}
                    </Observer>
                    <HeaderDescription className="ssh-keys-title-description">
                        {Resources.TitleDescription}
                        <Link
                            className="gen-ssh-key-link"
                            rel="nofollow noopener"
                            target="_blank"
                            aria-label={Resources.LearnMore}
                            href={Resources.HowToGenerateNewSSHPublicKey}
                        >
                            {Resources.LearnMore}
                        </Link>
                    </HeaderDescription>

                    <Observer message={this._errorMessage}>{(props: { message: string }) => props.message}</Observer>

                    <ServerFingerprintCard />

                    <Observer isFeatureEnabled={this._isFeatureEnabled} tokenData={this._tokenData}>
                        {(props: { isFeatureEnabled: boolean; tokenData: SSHToken[] }) =>
                            !props.isFeatureEnabled ? (
                                <DisabledSSHView />
                            ) : props.tokenData.length == 0 ? (
                                <SSHPublicKeysZeroData />
                            ) : (
                                <SSHPublicKeysTableView
                                    tokenData={this._tokenData}
                                    onShowKeyDetails={this._onShowKeyDetails}
                                    onDeleteKey={this._onShowDeleteDialog}
                                />
                            )
                        }
                    </Observer>
                </Page>
                <Observer showPanel={this._showAddPanel}>
                    {(props: { showPanel: boolean }) =>
                        props.showPanel && <AddNewKeyPanel onSave={this._onAddPublicKey} onDismiss={this._onDismissAddPanel} />
                    }
                </Observer>
                <Observer showPanel={this._showDetails}>
                    {(props: { showPanel: boolean }) =>
                        props.showPanel && <DisplaySSHKeyDataPanel onDismiss={this._onDismissDetailsPanel} selectedToken={this._selectedToken!} />
                    }
                </Observer>
                <Observer showDialog={this._showDeleteDialog}>
                    {(props: { showDialog: boolean }) =>
                        props.showDialog && (
                            <DeleteSSHKeyDialog
                                onConfirm={this._onDeletePublicKey}
                                selectedToken={this._selectedToken!}
                                onDismiss={this._onDismissDeleteDialog}
                            />
                        )
                    }
                </Observer>
            </Surface>
        );
    }

    private _fetchInitialData = async () => {
        const telemetryLogger = this._startActionCapture("Fetching Page Data and SSH Public Keys");
        try {
            const credentials = await SSHKeysDataSource.getCredentialsModel(this.context.pageContext);
            this._isFeatureEnabled.value = !credentials.publicKeysDisabled;
            if (!this._isFeatureEnabled.value) {
                return;
            }

            const restClient = getTokenClient(this.context.pageContext);
            const [pageData, tokens] = await Promise.all([
                SSHKeysDataSource.getInitialSSHData(this.context.pageContext),
                restClient.getSessionTokens(true, true)
            ]);
            if (!pageData || !tokens) {
                throw new Error("Failed to fetch Page Data or SSH Public Keys");
            }

            // Client side scope keys to org
            this._tokenData.value = tokens.reduce(function(filtered: SSHToken[], token: SessionToken) {
                if (token.targetAccounts.findIndex(x => equals(x, pageData.id, true)) > -1) {
                    filtered.push({ token, fingerprint: "" });
                }
                return filtered;
            }, []);
            this._pageData = pageData;

            this._markTimeToInteractive();
            this._fetchFingerprints();
        } catch (error) {
            telemetryLogger.captureError(error);
            this._errorMessage.value = this._getErrorMessage(error);
        }
    };

    // Lazy load fetch until fingerprint is added to Token Service
    private _fetchFingerprints = async () => {
        const telemetryLogger = this._startActionCapture("Fetch fingerprints");
        const { pageContext } = this.context;
        try {
            this._tokenData.value = await Promise.all(
                this._tokenData.value.map(async x => {
                    if (!x.fingerprint) {
                        const fingerprintData = await SSHKeysDataSource.getFingerprintData(pageContext, x.token.authorizationId);
                        x.fingerprint = fingerprintData ? fingerprintData.fingerprint : "";
                    }
                    return x;
                })
            );
        } catch (error) {
            telemetryLogger.captureError(error);
        }
    };

    private _onDeletePublicKey = async (sshToken: SSHToken) => {
        const telemetryLogger = this._startActionCapture("Delete SSH Public Key started");
        const { pageContext } = this.context;
        const { token } = sshToken;
        try {
            await getTokenClient(pageContext).revokeSessionToken(token.authorizationId, token.isPublic);
            this._tokenData.splice(
                this._tokenData.value.findIndex(x => x == sshToken),
                1
            );

            this._errorMessage.value = "";
            sendToast(localeFormat(Resources.ToastMessage, token.displayName, Resources.Removed), pageContext);
        } catch (error) {
            telemetryLogger.captureError(error);
            this._errorMessage.value = this._getErrorMessage(error, Resources.FailedToDeleteKey, token.displayName);
        } finally {
            this._onDismissDeleteDialog();
        }
    };

    private _onAddPublicKey = async (name: string, dataKey: string) => {
        const telemetryLogger = this._startActionCapture("Add SSH Public Key started");
        const { pageContext } = this.context;
        try {
            const service = pageContext.getService<IVssContributionService>("IVssContributionService");
            const token = await service.getDataAsync<SessionToken>(this.issueSessionTokenContributionId, this._getSessionToken(name, dataKey), true);
            if (!token) {
                throw new Error(service.getDataProviderExceptions()[this.issueSessionTokenContributionId].message);
            }

            this._tokenData.push({ token, fingerprint: "" });
            this._errorMessage.value = "";
            sendToast(localeFormat(Resources.ToastMessage, token.displayName, Resources.Added), pageContext);
            this._fetchFingerprints();
        } catch (error) {
            telemetryLogger.captureError(error);
            this._errorMessage.value = this._getErrorMessage(error, Resources.FailedToAddKey, name);
        } finally {
            this._onDismissAddPanel();
        }
    };

    private _getSessionToken(name: string, dataKey: string): SessionToken {
        const today = new Date();
        return {
            displayName: name,
            publicData: dataKey,
            validFrom: today,
            validTo: new Date(new Date(today).setFullYear(today.getFullYear() + 5)),
            scope: "app_token",
            targetAccounts: [this._pageData.id],
            isPublic: true
        } as SessionToken;
    }

    private _onShowDeleteDialog = (token: SSHToken) => {
        this._startActionCapture("Display delete dialog");
        this._selectedToken = token;
        this._showDeleteDialog.value = true;
    };

    private _onDismissDeleteDialog = () => {
        this._startActionCapture("Dismiss delete dialog");
        this._showDeleteDialog.value = false;
    };

    private _onShowKeyDetails = (token: SSHToken) => {
        this._startActionCapture("Display key data panel");
        this._selectedToken = token;
        this._showDetails.value = true;
    };

    private _onDismissDetailsPanel = () => {
        this._startActionCapture("Dismiss key view panel");
        this._showDetails.value = false;
    };

    private _onShowAddPanel = () => {
        this._startActionCapture("Display add new key panel");
        this._showAddPanel.value = true;
    };

    private _onDismissAddPanel = () => {
        this._startActionCapture("Dismiss add new key panel");
        this._showAddPanel.value = false;
    };

    private _getErrorMessage(error: any, title = "", ...args: any[]): string {
        let message = CleanErrorMessage((error.serverError && error.serverError.message) || error.serverError || error.message);
        switch (message) {
            case "DuplicateHash":
                message = Resources.DuplicateKey;
                break;
        }
        return (this._getMessageCard(`${title}${title && `: `}${message}`, ...args) as unknown) as string;
    }

    private _getMessageCard(message: string, ...args: any[]) {
        return (
            <MessageCard className="message-warning-card" severity={MessageCardSeverity.Error} onDismiss={() => (this._errorMessage.value = "")}>
                <FormatComponent format={message}>
                    {args.map((x, i) => (
                        <b key={`key-${i}`}>{x}</b>
                    ))}
                </FormatComponent>
            </MessageCard>
        );
    }

    private _startActionCapture(action: string, properties: { [key: string]: any } = {}): AdminFeatureTelemetryLogger {
        return this._telemetryLogger.startActionCapture(Feature.SSHPublicKeys, Feature.SSHPublicKeys, action, properties);
    }

    private _markTimeToInteractive() {
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(Area.Admin, Feature.SSHPublicKeys);
    }
}

Components.add("SSHPublicKeysView", SSHPublicKeysComponent);
